import React, { useState, useEffect, useRef, useCallback } from "react";
import { Layout } from "..";
import { Container, SharePopup } from "./styled";
import { addModal } from "../../lib/addModal";
import {
  UpgradeContainer,
  CanvasElement,
  GameContextSelector,
} from "../../components";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Loader, SharePopup as SharePupupComponent } from "../../components";
import { useLocation } from "react-router-dom";
import firebase from "../../lib/fire";
import secureLocalStorage from "react-secure-storage";
import { defaultItems } from "../../lib/defaultItems";
import { calculatePercj } from "../../lib/cabbStats";
import { generateId } from "../../lib/generateId";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  Title,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Radar, Bar } from "react-chartjs-2";
import { formatDate } from "../../lib/formatDate";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  Title,
  CategoryScale,
  LinearScale,
  BarElement
);

export const optionsBarChart = {
  responsive: true,
  plugins: {
    legend: {
      position: "top",
    },
    title: {
      display: false,
    },
  },
};

function Analytics({ appData }) {
  const [dataSets, setDataSets] = useState(null);
  const [shootingData, setShootingData] = useState(null);
  const [reboundingData, setReboundingData] = useState(null);
  const [assistsData, setAssistsData] = useState(null);
  const [extraData, setExtraData] = useState(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [isShare, setIsShare] = useState(false);
  const [isCollection, setIsCollection] = useState(false);
  const [clip, setClip] = useState(null);
  const [isSetClipName, setIsSetClipName] = useState(false);
  const [collectionName, setCollectionName] = useState([]);
  const [clipName, setClipName] = useState(null);
  const [popupStep, setPopupStep] = useState(null);
  const sessionUser = sessionStorage.getItem("user");
  const userId = secureLocalStorage.getItem("userId");
  const user = secureLocalStorage.getItem("user");
  const location = useLocation();
  const parsedUser = sessionUser ? JSON.parse(sessionUser) : null;
  let slug = location.pathname.replace("/app/analytics/", "");
  let cleanSlug = slug;
  let isClip;
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const [gameContextData, setGameContextData] = useState({
    activeLeague: null,
    activePhase: null,
    activeSeason: null,
    activeTeam: null,
    activeRival: null,
    baseTeam: null,
    isH2HActive: null,
    games: null,
    statInX: null,
    statInY: null,
    isLoading: false,
  });
  const [sharedData, setSharedData] = useState({
    emails: [],
    inputValue: null,
  });

  if (slug.includes("C-")) {
    isClip = true;
    cleanSlug = slug.replace("C-", "");
  } else {
    isClip = false;
  }

  const handleGameContextChange = useCallback((contextData) => {
    setGameContextData((prev) => ({
      ...prev,
      ...contextData,
    }));
  }, []);

  useEffect(() => {
    const fetchClip = async () => {
      if (!isClip) return;

      const parsedUser = JSON.parse(sessionUser);
      let clipPlaceholder = null;
      let clipSource = null;

      // Buscar en analyticsClips
      if (parsedUser.analyticsClips) {
        const found = Object.values(parsedUser.analyticsClips).find(
          (item) => item.id === cleanSlug
        );

        if (found) {
          clipPlaceholder = found;
          clipSource = "analytics";
          clipPlaceholder.board = Object.values(
            parsedUser.analyticsBoards ?? {}
          )?.[clipPlaceholder?.templateId];
        }
      }

      // Buscar en sharedClips
      if (!clipPlaceholder && parsedUser.sharedClips) {
        const foundShared = Object.values(parsedUser.sharedClips).find(
          (item) => item.id === cleanSlug
        );

        if (foundShared) {
          clipPlaceholder = foundShared;
          clipSource = "shared";
        }
      }

      // Buscar en sharedCollections
      if (!clipPlaceholder && parsedUser.sharedCollections) {
        const collections = Object.values(parsedUser.sharedCollections);

        for (const collection of collections) {
          const clipFromCollection =
            (Array.isArray(collection.clipIds) &&
              collection.clipIds.find((clip) => clip.id === cleanSlug)) ||
            (collection.clips && collection.clips[cleanSlug]
              ? { id: cleanSlug }
              : null);

          if (clipFromCollection) {
            clipPlaceholder = clipFromCollection;
            clipSource = "sharedCollection";
            break;
          }
        }
      }

      await setClip(clipPlaceholder);

      if (clipPlaceholder?.config?.filters) {
        await setGameContextData((prev) => ({
          ...prev,
          ...clipPlaceholder.config.filters,
        }));
      }

      // 🔥 NUEVO: cargar colecciones donde aparece el clip
      const userCollections = parsedUser.clipCollections || {};
      const collectionsArray = Object.values(userCollections);

      const clipCollections = collectionsArray
        .filter(
          (col) => Array.isArray(col.clipIds) && col.clipIds.includes(cleanSlug)
        )
        .map((col) => col.name);

      setCollectionName(clipCollections); // ← guarda nombres
    };

    fetchClip();
  }, [isClip, cleanSlug, sessionUser]);

  const options = {
    responsive: true,
    hover: {
      backgroundColor: "rgba(255, 99, 132, 0.6)",
    },
    scales: {
      r: {
        suggestedMin: 10,
      },
    },
  };

  const calculateMaxHeight = (data) => {
    return Math.max(...data?.map((item) => item.y + item.height)) + 250;
  };

  useEffect(() => {
    if (appData) setIsLoading(false);
  }, [appData]);

  useEffect(() => {
    if (
      gameContextData.activeTeam !== null ||
      gameContextData.activeRival !== null
    ) {
      const newData = {
        labels: [
          "Triples convertidos por partido",
          "Dobles convertidos por partido",
          "Libres convertidos por partido",
          "Rebotes por partido",
          "Asistencias por partido",
          "Recuperos por partido",
        ],
        datasets: [
          {
            label: appData?.teams?.filter(
              (e) => e._id === gameContextData.activeTeam
            )[0].name,
            data: [
              calculateMetric(gameContextData.activeTeam, "THREEP"),
              calculateMetric(gameContextData.activeTeam, "TWOOP"),
              calculateMetric(gameContextData.activeTeam, "FT"),
              calculateMetric(gameContextData.activeTeam, "REB"),
              calculateMetric(gameContextData.activeTeam, "ASS"),
              calculateMetric(gameContextData.activeTeam, "STL"),
            ],
            backgroundColor: "rgba(255, 99, 132, 0.2)",
            borderColor: "rgba(255, 99, 132, 0.6)",
            borderWidth: 1,
            lineTension: 0.3,
            hoverBackgroundColor: "red",
          },
          {
            label:
              gameContextData.activeRival !== null
                ? appData?.teams?.filter(
                    (e) => e._id === gameContextData.activeRival
                  )[0].name
                : "Selecciona rival",
            data: [
              calculateMetric(gameContextData.activeRival, "THREEP"),
              calculateMetric(gameContextData.activeRival, "TWOOP"),
              calculateMetric(gameContextData.activeRival, "FT"),
              calculateMetric(gameContextData.activeRival, "REB"),
              calculateMetric(gameContextData.activeRival, "ASS"),
              calculateMetric(gameContextData.activeRival, "STL"),
            ],
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            borderColor: "rgba(0, 0, 0, 0.6)",
            borderWidth: 1,
            lineTension: 0.3,
            hoverBackgroundColor: "red",
          },
        ],
      };
      setDataSets(newData);
      const shootingData = {
        labels: [
          "Procentaje de 3 puntos",
          "Procentaje de 2 puntos",
          "Procentaje de 1 puntos",
          "Procentaje de tiros de campo",
          "Porcentaje real de tiro",
        ],
        datasets: [
          {
            label: appData?.teams?.filter(
              (e) => e._id === gameContextData.activeTeam
            )[0].name,
            data: [
              calculateMetric(gameContextData.activeTeam, "THREEPP"),
              calculateMetric(gameContextData.activeTeam, "TWOOPP"),
              calculateMetric(gameContextData.activeTeam, "FTP"),
              calculateMetric(gameContextData.activeTeam, "FG"),
              calculateMetric(gameContextData.activeTeam, "TSP"),
            ],
            backgroundColor: "rgba(255, 99, 132, 0.2)",
            borderColor: "rgba(255, 99, 132, 0.6)",
            borderWidth: 1,
            lineTension: 0.3,
            hoverBackgroundColor: "red",
          },
          {
            label:
              gameContextData.activeRival !== null
                ? appData?.teams?.filter(
                    (e) => e._id === gameContextData.activeRival
                  )[0].name
                : "Selecciona rival",
            data: [
              calculateMetric(gameContextData.activeRival, "THREEPP"),
              calculateMetric(gameContextData.activeRival, "TWOOPP"),
              calculateMetric(gameContextData.activeRival, "FTP"),
              calculateMetric(gameContextData.activeRival, "FG"),
              calculateMetric(gameContextData.activeRival, "TSP"),
            ],
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            borderColor: "rgba(0, 0, 0, 0.6)",
            borderWidth: 1,
            lineTension: 0.3,
            hoverBackgroundColor: "red",
          },
        ],
      };
      setShootingData(shootingData);
      const reboundingData = {
        labels: [
          "Rebotes ofensivos",
          "Rebotes defensivos",
          "Rebotes totales",
          "Porcentaje de rebotes ofensivos",
          "Porcentaje de rebotes defensivos",
        ],
        datasets: [
          {
            label: appData?.teams?.filter(
              (e) => e._id === gameContextData.activeTeam
            )[0].name,
            data: [
              calculateMetric(gameContextData.activeTeam, "ORB"),
              calculateMetric(gameContextData.activeTeam, "DRB"),
              calculateMetric(gameContextData.activeTeam, "REB"),
              calculateMetric(gameContextData.activeTeam, "OREB"),
              calculateMetric(gameContextData.activeTeam, "DREB"),
            ],
            backgroundColor: "rgba(255, 99, 132, 0.2)",
            borderColor: "rgba(255, 99, 132, 0.6)",
            borderWidth: 1,
            lineTension: 0.3,
            hoverBackgroundColor: "red",
          },
          {
            label:
              gameContextData.activeRival !== null
                ? appData?.teams?.filter(
                    (e) => e._id === gameContextData.activeRival
                  )[0].name
                : "Selecciona rival",
            data: [
              calculateMetric(gameContextData.activeRival, "ORB"),
              calculateMetric(gameContextData.activeRival, "DRB"),
              calculateMetric(gameContextData.activeRival, "REB"),
              calculateMetric(gameContextData.activeRival, "OREB"),
              calculateMetric(gameContextData.activeRival, "DREB"),
            ],
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            borderColor: "rgba(0, 0, 0, 0.6)",
            borderWidth: 1,
            lineTension: 0.3,
            hoverBackgroundColor: "red",
          },
        ],
      };
      setReboundingData(reboundingData);
      const assitsData = {
        labels: [
          "Asistencias por partido",
          "Recuperos por partido",
          "Perdidas por partido",
        ],
        datasets: [
          {
            label: appData?.teams?.filter(
              (e) => e._id === gameContextData.activeTeam
            )[0].name,
            data: [
              calculateMetric(gameContextData.activeTeam, "ASS"),
              calculateMetric(gameContextData.activeTeam, "STL"),
              calculateMetric(gameContextData.activeTeam, "TO"),
            ],
            backgroundColor: "rgba(255, 99, 132, 0.2)",
            borderColor: "rgba(255, 99, 132, 0.6)",
            borderWidth: 1,
            lineTension: 0.3,
            hoverBackgroundColor: "red",
          },
          {
            label:
              gameContextData.activeRival !== null
                ? appData?.teams?.filter(
                    (e) => e._id === gameContextData.activeRival
                  )[0].name
                : "Selecciona rival",
            data: [
              calculateMetric(gameContextData.activeRival, "ASS"),
              calculateMetric(gameContextData.activeRival, "STL"),
              calculateMetric(gameContextData.activeRival, "TO"),
            ],
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            borderColor: "rgba(0, 0, 0, 0.6)",
            borderWidth: 1,
            lineTension: 0.3,
            hoverBackgroundColor: "red",
          },
        ],
      };
      setAssistsData(assitsData);
      const extraData = {
        labels: [
          "Faltas cometidas",
          "Faltas recibidas",
          "Tapas cometidas",
          "Tapas recibidas",
        ],
        datasets: [
          {
            label: appData?.teams?.filter(
              (e) => e._id === gameContextData.activeTeam
            )[0].name,
            data: [
              calculateMetric(gameContextData.activeTeam, "FL"),
              calculateMetric(gameContextData.activeTeam, "FLR"),
              calculateMetric(gameContextData.activeTeam, "BLK"),
              calculateMetric(gameContextData.activeTeam, "BLKR"),
            ],
            backgroundColor: "rgba(255, 99, 132, 0.2)",
            borderColor: "rgba(255, 99, 132, 0.6)",
            borderWidth: 1,
            lineTension: 0.3,
            hoverBackgroundColor: "red",
          },
          {
            label:
              gameContextData.activeRival !== null
                ? appData?.teams?.filter(
                    (e) => e._id === gameContextData.activeRival
                  )[0].name
                : "Selecciona rival",
            data: [
              calculateMetric(gameContextData.activeRival, "FL"),
              calculateMetric(gameContextData.activeRival, "FLR"),
              calculateMetric(gameContextData.activeRival, "BLK"),
              calculateMetric(gameContextData.activeRival, "BLKR"),
            ],
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            borderColor: "rgba(0, 0, 0, 0.6)",
            borderWidth: 1,
            lineTension: 0.3,
            hoverBackgroundColor: "red",
          },
        ],
      };
      setExtraData(extraData);
    }
  }, [
    gameContextData,
    gameContextData.activeTeam,
    gameContextData.activeRival,
    gameContextData.activeLeague,
    gameContextData.activePhase,
    gameContextData.activeSeason,
    gameContextData.isH2HActive,
    gameContextData.games,
  ]);

  const calculateTeamPosition = (teamID) => {
    if (gameContextData.activeLeague !== null) {
      let teamsArr = [];
      appData.teams?.forEach((team) => {
        team.history.some(
          (t) =>
            t.season === gameContextData.activeSeason &&
            t.league === gameContextData.activeLeague
        );
        if (
          team.history.some(
            (t) =>
              t.season === gameContextData.activeSeason &&
              t.league === gameContextData.activeLeague
          )
        ) {
          teamsArr.push({
            _id: team._id,
            name: team.name,
            gamesWon: 0,
            gamesLost: 0,
            totalPoints: 0,
            pointsMade: 0,
            pointsRecibed: 0,
            zone: team.zone === "sur" ? "sur" : "norte",
          });
        }
      });

      for (let index = 0; index < gameContextData.games?.length; index++) {
        if (
          gameContextData.games[index].category ===
            gameContextData.activeLeague &&
          gameContextData.games[index].season ===
            gameContextData.activeSeason &&
          gameContextData.games[index].stage === "Fase regular"
        ) {
          let localTotalPts = 0;
          let visitTotalPts = 0;
          const localTeam = teamsArr?.find(
            (object) => object._id === gameContextData.games[index].local_team
          );
          gameContextData.games[index].local_stats.forEach((element) => {
            localTotalPts += Number(element.pts);
          });
          const visitTeam = teamsArr?.find(
            (object) => object._id === gameContextData.games[index].visit_team
          );
          gameContextData.games[index].visit_stats.forEach((element) => {
            visitTotalPts += element.pts;
          });
          localTeam.pointsMade += localTotalPts;
          localTeam.pointsRecibed += visitTotalPts;
          visitTeam.pointsMade += visitTotalPts;
          visitTeam.pointsRecibed += localTotalPts;
          if (localTotalPts > visitTotalPts) {
            localTeam.gamesWon++;
            visitTeam.gamesLost++;
          } else {
            localTeam.gamesLost++;
            visitTeam.gamesWon++;
          }
        }
      }
      teamsArr.forEach((element) => {
        element.totalPoints = element.gamesWon * 2 + element.gamesLost;
      });
      teamsArr.sort(function (a, b) {
        return (
          b.totalPoints - a.totalPoints ||
          b.gamesWon - a.gamesWon ||
          a.gamesLost - b.gamesLost ||
          b.pointsMade - a.pointsMade ||
          a.pointsRecibed - b.pointsRecibed
        );
      });

      const indexOfObject =
        gameContextData.activeLeague === "LA"
          ? teamsArr
              .filter(
                (e) =>
                  e.zone ===
                  teamsArr.filter(
                    (e) => e._id === gameContextData.activeTeam
                  )[0].zone
              )
              .findIndex((object) => {
                return object._id === teamID;
              })
          : teamsArr.findIndex((object) => {
              return object._id === teamID;
            });
      return indexOfObject;
    }
  };

  function log5(home, away) {
    if (home === 0 || away === 0) {
      return 0;
    }
    var prob = (home - home * away) / (home + away - 2 * home * away);
    return prob;
  }

  function calculateProbability() {
    var activeTeamHomeWins = 0;
    var activeTeamHomeGames = 0;
    var activeTeamTotalWins = 0;
    var activeTeamTotalGames = 0;

    var activeRivalAwayWins = 0;
    var activeRivalAwayGames = 0;
    var activeRivalTotalWins = 0;
    var activeRivalTotalGames = 0;

    var activeTeamStreak = 0 / 0;
    var activeRivalStreak = 0 / 0;
    for (let index = 0; index < gameContextData.games?.length; index++) {
      if (
        gameContextData.games[index].category ===
          gameContextData.activeLeague &&
        gameContextData.games[index].season === gameContextData.activeSeason
      ) {
        let localTotalPts = 0;
        let visitTotalPts = 0;
        if (
          gameContextData.games[index].local_team === gameContextData.activeTeam
        ) {
          gameContextData.games[index].local_stats.forEach((element) => {
            localTotalPts += element.pts;
          });
          gameContextData.games[index].visit_stats.forEach((element) => {
            visitTotalPts += element.pts;
          });
          if (localTotalPts > visitTotalPts) {
            activeTeamHomeWins++;
            activeTeamTotalWins++;
          }
          activeTeamHomeGames++;
          activeTeamTotalGames++;
        } else if (
          gameContextData.games[index].visit_team === gameContextData.activeTeam
        ) {
          gameContextData.games[index].local_stats.forEach((element) => {
            localTotalPts += element.pts;
          });
          gameContextData.games[index].visit_stats.forEach((element) => {
            visitTotalPts += element.pts;
          });
          if (localTotalPts < visitTotalPts) {
            activeTeamTotalWins++;
          }
          activeTeamTotalGames++;
        } else if (
          gameContextData.games[index].visit_team ===
          gameContextData.activeRival
        ) {
          gameContextData.games[index].local_stats.forEach((element) => {
            localTotalPts += element.pts;
          });
          gameContextData.games[index].visit_stats.forEach((element) => {
            visitTotalPts += element.pts;
          });
          if (localTotalPts < visitTotalPts) {
            activeRivalAwayWins++;
            activeTeamTotalWins++;
          }
          activeRivalAwayGames++;
          activeRivalTotalGames++;
        } else if (
          gameContextData.games[index].local_team ===
          gameContextData.activeRival
        ) {
          gameContextData.games[index].local_stats.forEach((element) => {
            localTotalPts += element.pts;
          });
          gameContextData.games[index].visit_stats.forEach((element) => {
            visitTotalPts += element.pts;
          });
          if (localTotalPts > visitTotalPts) {
            activeRivalTotalWins++;
          }
          activeRivalTotalGames++;
        }
      }
    }

    var activeTeamStreakTotalWins = 0;
    var activeRivalStreakTotalWins = 0;
    var activeRivalStreakTotal = gameContextData.games?.filter(
      (game) =>
        (game.local_team === gameContextData.activeRival ||
          game.visit_team === gameContextData.activeRival) &&
        game.season === gameContextData.activeSeason
    );
    activeRivalStreakTotal = activeRivalStreakTotal.slice(-1 * 10);

    var activeTeamStreakTotal = gameContextData.games.filter(
      (game) =>
        (game.local_team === gameContextData.activeTeam ||
          game.visit_team === gameContextData.activeTeam) &&
        game.season === gameContextData.activeSeason
    );
    activeTeamStreakTotal = activeTeamStreakTotal.slice(-1 * 10);

    for (let index = 0; index < activeTeamStreakTotal.length; index++) {
      let localTotalPts = 0;
      let visitTotalPts = 0;
      if (
        activeTeamStreakTotal[index].local_team === gameContextData.activeTeam
      ) {
        activeTeamStreakTotal[index].local_stats.forEach((element) => {
          localTotalPts += element.pts;
        });
        activeTeamStreakTotal[index].visit_stats.forEach((element) => {
          visitTotalPts += element.pts;
        });
        if (localTotalPts > visitTotalPts) {
          activeTeamStreakTotalWins++;
        }
      } else if (
        activeTeamStreakTotal[index].visit_team === gameContextData.activeTeam
      ) {
        activeTeamStreakTotal[index].local_stats.forEach((element) => {
          localTotalPts += element.pts;
        });
        activeTeamStreakTotal[index].visit_stats.forEach((element) => {
          visitTotalPts += element.pts;
        });
        if (localTotalPts < visitTotalPts) {
          activeTeamStreakTotalWins++;
        }
      }
    }
    for (let index = 0; index < activeRivalStreakTotal.length; index++) {
      let localTotalPts = 0;
      let visitTotalPts = 0;
      if (
        activeRivalStreakTotal[index].visit_team === gameContextData.activeRival
      ) {
        activeRivalStreakTotal[index].local_stats.forEach((element) => {
          localTotalPts += element.pts;
        });
        activeRivalStreakTotal[index].visit_stats.forEach((element) => {
          visitTotalPts += element.pts;
        });
        if (localTotalPts < visitTotalPts) {
          activeRivalStreakTotalWins++;
        }
      } else if (
        activeRivalStreakTotal[index].local_team === gameContextData.activeRival
      ) {
        activeRivalStreakTotal[index].local_stats.forEach((element) => {
          localTotalPts += element.pts;
        });
        activeRivalStreakTotal[index].visit_stats.forEach((element) => {
          visitTotalPts += element.pts;
        });
        if (localTotalPts > visitTotalPts) {
          activeRivalStreakTotalWins++;
        }
      }
    }

    var activeTeamHome = isNaN(activeTeamHomeWins / activeTeamHomeGames)
      ? 0
      : activeTeamHomeWins / activeTeamHomeGames;
    var activeTeamTotal = isNaN(activeTeamTotalWins / activeTeamTotalGames)
      ? 0
      : activeTeamTotalWins / activeTeamTotalGames;
    var activeRivalAway = isNaN(activeRivalAwayWins / activeRivalAwayGames)
      ? 0
      : activeRivalAwayWins / activeRivalAwayGames;
    var activeRivalTotal = isNaN(activeRivalTotalWins / activeRivalTotalGames)
      ? 0
      : activeRivalTotalWins / activeRivalTotalGames;
    var activeTeamStreak = activeTeamStreakTotalWins / 10;
    var activeRivalStreak = activeRivalStreakTotalWins / 10;

    var prob =
      0.6 * log5(activeTeamHome, activeRivalAway) +
      0.3 * log5(activeTeamTotal, activeRivalTotal) +
      0.1 * log5(activeTeamStreak, activeRivalStreak);

    return prob * 100;
  }

  const generateImageCanvas = async () => {
    let dataContainer = document.getElementById("data-container");
    if (!dataContainer) return;

    dataContainer.classList.add("to-print");

    let imageButtons = document.querySelectorAll("#change-image-btn");
    imageButtons.forEach((img) => (img.style.display = "none"));

    const elements = document.querySelectorAll(
      '[id^="Featured"], [id^="TeamPerformance"], [id^="BasicTeamStats"], [id^="AdvancedTeamStats"], [id^="ComparisonBarChart"], [id^="Title"], [id^="Note"], [id^="statisticsTable"],[id^="ComparisonPlayerChart"],[id^="PlayerResume"],[id^="PlayerStatistics"],[id^="TeamFourFactorsComparison"],[id^="TeamLineupNaturalStats"],[id^="TeamLineupFourFactoesStats"],[id^="LineupsAdvanceStats"]'
    );
    elements.forEach((element) => {
      element.style.border = "1px solid black";
    });

    await loadimages();

    const scrollX = dataContainer.scrollLeft;
    const scrollY = dataContainer.scrollTop;

    const canvas = await html2canvas(dataContainer, {
      //scale: 2,
      scale: window.innerWidth < 768 ? 1 : 2, // Lower scale on mobile
      useCORS: true,
      allowTaint: true, // Allow tainted canvas if CORS fails
      logging: false,
      scrollX,
      scrollY,
      windowWidth: dataContainer.scrollWidth,
      windowHeight: dataContainer.scrollHeight,
      width: dataContainer.scrollWidth,
      height: dataContainer.scrollHeight,
    });

    const dataUrl = canvas.toDataURL("image/jpeg");
    const link = document.createElement("a");
    link.download = `${
      appData?.teams?.find((e) => e._id === gameContextData.activeTeam)?.name
    } vs ${
      appData?.teams?.find((e) => e._id === gameContextData.activeRival)?.name
    }.jpeg`;
    link.href = dataUrl;
    link.click();

    dataContainer.classList.remove("to-print");

    elements.forEach((element) => {
      element.style.border = "unset";
    });

    document.getElementById("print-buttons-options").classList.remove("active");
  };

  const loadimages = () => {
    const images = document.querySelectorAll("#data-container img");
    const promises = Array.from(images).map((img) => {
      return new Promise((resolve) => {
        img.addEventListener("load", resolve);
      });
    });

    Promise.all(promises).then(() => {
      setImagesLoaded(true);
    });
  };

  const addHeader = (doc, appData, activeTeam) => {
    if (
      gameContextData.baseTeam !== null &&
      gameContextData.baseTeam !== undefined
    ) {
      const imgProps = doc.getImageProperties(
        `/assets/teams-logos/${
          appData?.teams?.find((e) => e._id === gameContextData.baseTeam)?.name
        }.png`
      );
      doc.addImage(
        `/assets/teams-logos/${
          appData?.teams?.find((e) => e._id === gameContextData.baseTeam)?.name
        }.png`,
        "PNG",
        100,
        10,
        80,
        (imgProps.height * 80) / imgProps.width
      );
    }
    doc.setFontSize(20);
    doc.setTextColor(0, 0, 0);
    doc.text(
      `Temporada ${gameContextData.activeSeason}`,
      doc.internal.pageSize.getWidth() / 2,
      40,
      { align: "center", baseline: "middle" }
    );
    if (activeTeam !== null && activeTeam !== undefined) {
      const imgLeague = doc.getImageProperties(
        `/assets/logo${
          appData?.teams?.find((e) => e._id === activeTeam)?.league
        }.png`
      );
      doc.addImage(
        `/assets/logo${
          appData?.teams?.find((e) => e._id === activeTeam)?.league
        }.png`,
        "PNG",
        doc.internal.pageSize.getWidth() - 140,
        10,
        80,
        (imgLeague.height * 80) / imgLeague.width
      );
    }
    return 100;
  };

  const handleSplitAndDownload = async () => {
    const element = containerRef.current;
    if (!element) return;
    setIsExporting(true);
    let dataContainer = await document.getElementById("data-container");
    const pdfWidth = dataContainer.scrollWidth;
    const margin = 10;
    const imgWidth = pdfWidth - 2 * margin;
    const pageHeight = 1500;
    let orientation = pdfWidth > pageHeight ? "l" : "p";

    const doc = new jsPDF(orientation, "pt", [imgWidth, pageHeight]);
    const pageWidth = doc.internal.pageSize.getWidth(); // Ancho de la página

    //COVER
    doc.setFontSize(32);
    doc.setFont(undefined, "bold");

    doc.text(
      !isClip ? JSON.parse(sessionUser).analyticsBoards[slug]?.name : clip.name,
      pageWidth / 2,
      200,
      { align: "center" }
    );

    doc.setFontSize(24);
    doc.text(`TEMPORADA ${gameContextData.activeSeason}`, pageWidth / 2, 250, {
      align: "center",
    });
    let ownTeam =
      gameContextData.baseTeam === gameContextData.activeTeam
        ? gameContextData.activeRival
        : gameContextData.activeTeam;
    doc.text(
      `FECHA ${
        gameContextData.games?.filter(
          (game) =>
            (game.local_team === ownTeam || game.visit_team === ownTeam) &&
            game.season === gameContextData.activeSeason
        ).length
      }`,
      pageWidth / 2,
      280,
      {
        align: "center",
      }
    );
    if (
      gameContextData.activeTeam !== null &&
      gameContextData.activeTeam !== undefined
    ) {
      const imgProps = doc.getImageProperties(
        `/assets/teams-logos/${
          appData?.teams?.find((e) => e._id === gameContextData.activeTeam)
            ?.name
        }.png`
      );
      doc.addImage(
        `/assets/teams-logos/${
          appData?.teams?.find((e) => e._id === gameContextData.activeTeam)
            ?.name
        }.png`,
        "PNG",
        pageWidth / 2 - 275,
        500,
        150,
        (imgProps.height * 150) / imgProps.width
      );
      doc.addImage(
        `/assets/logo${
          appData?.teams?.find((e) => e._id === gameContextData.activeTeam)
            ?.league
        }.png`,
        "PNG",
        pageWidth / 2 - 75 + 200,
        500,
        120,
        (imgProps.height * 120) / imgProps.width
      );
    }
    if (
      gameContextData.baseTeam !== null &&
      gameContextData.baseTeam !== undefined
    ) {
      doc.setFillColor(
        appData?.teams?.find((e) => e._id === gameContextData.baseTeam)
          ?.team_primary_color
      );
      doc.roundedRect(pageWidth / 4, 800, pageWidth / 2, 180, 10, 10, "F");
      doc.setTextColor(
        appData?.teams?.find((e) => e._id === gameContextData.baseTeam)
          ?.team_secondary_color
      );
      doc.setFontSize(30);
      doc.text(
        appData?.teams?.find((e) => e._id === gameContextData.baseTeam)?.name,
        pageWidth / 4 + 200,
        860
      );
      doc.setFontSize(24);
      doc.text(
        appData?.teams?.find((e) => e._id === gameContextData.baseTeam)?.city,
        580,
        910
      );
    } else {
      doc.text("No hay equipo base seleccionado", 580, 910);
    }

    if (
      gameContextData.baseTeam !== null &&
      gameContextData.baseTeam !== undefined
    ) {
      const imgProps = doc.getImageProperties(
        `/assets/teams-logos/${
          appData?.teams?.find((e) => e._id === gameContextData.baseTeam)?.name
        }.png`
      );
      doc.addImage(
        `/assets/teams-logos/${
          appData?.teams?.find((e) => e._id === gameContextData.baseTeam)?.name
        }.png`,
        "PNG",
        pageWidth / 4 + 50,
        810,
        120,
        (imgProps.height * 120) / imgProps.width
      );
    }

    await loadimages();
    //CONTENT

    const imageButtons = document.querySelectorAll("#change-image-btn");
    imageButtons.forEach((img) => (img.style.display = "none"));
    const linkButtons = document.querySelectorAll("#link-button");
    linkButtons.forEach((link) => (link.style.display = "none"));
    const linkText = document.querySelectorAll("#link-content");
    linkText.forEach((link) => (link.style.display = "none"));

    const elements = document.querySelectorAll(
      '[id^="Featured"], [id^="TeamPerformance"], [id^="BasicTeamStats"], [id^="AdvancedTeamStats"], [id^="ComparisonBarChart"], [id^="Title"], [id^="Note"], [id^="statisticsTable"],[id^="ComparisonPlayerChart"],[id^="PlayerResume"],[id^="PlayerStatistics"],[id^="TeamFourFactorsComparison"],[id^="TeamLineupNaturalStats"],[id^="TeamLineupFourFactoesStats"],[id^="LineupsAdvanceStats"]'
    );

    elements.forEach((element) => {
      if (!element.id.includes("Image"))
        element.style.border = "1px solid black";
      if (
        element.id.includes("ComparisonPlayerChart") ||
        element.id.includes("PlayerResume") ||
        element.id.includes("PlayerStatistics")
      ) {
        const select = element.querySelector("select");
        if (select) {
          select.style.display = "none";
        }
      }
      if (element.id.includes("statisticsTable")) {
        // Seleccionar todas las tablas
        const tables = element.querySelectorAll("table");

        tables.forEach((table) => {
          // Procesar encabezados (thead)
          const headerRows = table.querySelectorAll("thead tr");
          headerRows.forEach((row) => {
            const headerCells = row.querySelectorAll("th");
            headerCells.forEach((cell, index) => {
              if (index === 0) {
                cell.style.textAlign = "left";
              } else {
                cell.style.textAlign = "center";
              }
            });
          });

          // Procesar cuerpo de la tabla (tbody)
          const bodyRows = table.querySelectorAll("tbody tr");
          bodyRows.forEach((row) => {
            const bodyCells = row.querySelectorAll("td");
            bodyCells.forEach((cell, index) => {
              if (index === 0) {
                cell.style.textAlign = "left";
              } else {
                cell.style.textAlign = "center";
              }
            });
          });
        });
      }
    });

    const pages = [];
    let currentPage = [];
    let currentHeight = 0;

    function isTitle(el) {
      return el.id.startsWith("Title");
    }

    function groupByTitle(elements) {
      const groups = [];
      let currentGroup = null;

      const sortedElements = [...elements].sort((a, b) => {
        const topA = parseFloat(a.style.top) || 0;
        const topB = parseFloat(b.style.top) || 0;
        return topA - topB;
      });

      sortedElements.forEach((el) => {
        if (isTitle(el)) {
          if (currentGroup) groups.push(currentGroup);
          currentGroup = [el];
        } else {
          if (!currentGroup) currentGroup = [];
          currentGroup.push(el);
        }
      });

      if (currentGroup) groups.push(currentGroup);
      return groups;
    }

    const groups = await groupByTitle(elements);
    const container = document.getElementById("data-container");
    const containerTop = container.getBoundingClientRect().top;
    let pageOffset = 0;

    groups.forEach((group) => {
      const verticalLines = [];

      group.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const top = rect.top - containerTop;
        const bottom = rect.bottom - containerTop;
        const height = rect.height;

        let placed = false;

        for (const line of verticalLines) {
          const overlap =
            Math.min(line.bottom, bottom) - Math.max(line.top, top);
          if (overlap > 0) {
            line.top = Math.min(line.top, top);
            line.bottom = Math.max(line.bottom, bottom);
            line.height = Math.max(line.height, height);
            placed = true;
            break;
          }
        }

        if (!placed) {
          verticalLines.push({ top, bottom, height });
        }
      });

      const total = verticalLines.reduce((sum, line) => sum + line.height, 0);
      const maxBottom = Math.max(...verticalLines.map((line) => line.bottom));
      const minTop = Math.min(...verticalLines.map((line) => line.top));

      if (maxBottom - pageOffset <= pageHeight) {
        currentPage.push(...group);
        currentHeight += total;
      } else {
        if (currentPage.length > 0) {
          pages.push([...currentPage]);
          currentPage = [];
          currentHeight = 0;
        }
        currentPage.push(...group);
        currentHeight = total;
        pageOffset = minTop;
      }
    });

    if (currentPage.length > 0) {
      pages.push([...currentPage]);
    }
    const linkElements = [];

    for (let i = 0; i < pages.length; i++) {
      doc.addPage([imgWidth, pageHeight]);

      const tempContainer = document.createElement("div");
      tempContainer.style.width = `${element.scrollWidth}px`;
      tempContainer.style.minHeight = "1500px";
      tempContainer.style.position = "absolute";
      tempContainer.style.left = "-9999px";
      tempContainer.style.background = "white";
      document.body.appendChild(tempContainer);

      let pageElements = pages[i].map((el) => {
        const clone = el.cloneNode(true);
        const originalTop = Number.parseFloat(el.style.top) || 0;
        const originalLeft = Number.parseFloat(el.style.left) || 0;

        // Check if this is a link element
        if (el.id.startsWith("Link")) {
          const linkElement =
            el.getElementsByClassName("title-link-wrapper")[0];
          if (linkElement) {
            linkElements.push({
              href: linkElement.getAttribute("data-link"),
              text: linkElement.textContent,
              top: originalTop,
              left: originalLeft,
              width: el.offsetWidth,
              height: el.offsetHeight,
              page: i,
            });
          }
        }

        if (el.id.startsWith("ComparisonBarChart")) {
          const canvas = el.querySelector("canvas");
          if (canvas) {
            const chartImg = canvas.toDataURL("image/png");
            const img = document.createElement("img");
            img.src = chartImg;
            img.style.width = canvas.style.width;
            img.style.height = canvas.style.height;
            img.style.position = "absolute";
            img.style.top = el.style.top;
            img.style.left = el.style.left;
            img.style.border = "1px solid black";
            return { clone: img, top: originalTop, left: originalLeft };
          }
        }

        return { clone, top: originalTop, left: originalLeft };
      });

      if (i === 0) {
        pageElements.forEach((el) => {
          el.clone.style.top = `${el.top}px`;
          el.clone.style.height = `${el.height}px`;
          el.clone.style.left = `${el.left}px`;
          tempContainer.appendChild(el.clone);
        });
      } else {
        const minTop = Math.min(...pageElements.map((el) => el.top));

        pageElements = pageElements.map((el) => ({
          ...el,
          top: el.top - minTop,
        }));

        pageElements.sort((a, b) => a.top - b.top);

        let accumulatedTop = 0;
        pageElements.forEach((el, index) => {
          if (index === 0) {
            el.clone.style.top = `${100}px`;
          } else {
            el.clone.style.top = `${el.top + 100}px`;
          }
          el.clone.style.left = `${el.left}px`;
          tempContainer.appendChild(el.clone);
        });
      }

      // Add this before calling html2canvas
      const tables = tempContainer.querySelectorAll("table");
      tables.forEach((table) => {
        table.style.width = "100%";
        table.style.height = "100%";
      });

      await new Promise((resolve) => setTimeout(resolve, 500));

      const canvas = await html2canvas(tempContainer, {
        allowTaint: true,
        useCORS: true,
        scale: 2,
        logging: false,
        width: tempContainer.scrollWidth,
        height: tempContainer.scrollHeight,
      });

      await addHeader(
        doc,
        appData,
        gameContextData.activeTeam,
        gameContextData.activeRival
      );

      const imgData = canvas.toDataURL("image/jpeg", 1.0);

      doc.addImage(
        imgData,
        "JPEG",
        margin,
        margin + 100,
        imgWidth,
        pageHeight - 100
      );

      for (const link of linkElements) {
        if (link.page === i) {
          doc.setFont(undefined, "bold");
          if (link.href !== "" && link.page === i) {
            doc.setTextColor(0, 0, 255);
          } else {
            doc.setTextColor(0, 0, 0);
          }
          doc.setFontSize(29);
          doc.textWithLink(link.text, link.left + 20, link.top + 145, {
            url: link.href,
            target: "_blank",
          });
        }
      }
      document.body.removeChild(tempContainer);
    }

    doc.setTextColor(0, 0, 0);

    //CLOSING COVER
    doc.addPage([imgWidth, pageHeight]);

    doc.setFontSize(32);
    doc.text(`TEMPORADA ${gameContextData.activeSeason}`, pageWidth / 2, 250, {
      align: "center",
    });

    if (
      gameContextData.activeTeam !== null &&
      gameContextData.activeTeam !== undefined
    ) {
      const imgProps = doc.getImageProperties(
        `/assets/teams-logos/${
          appData?.teams?.find((e) => e._id === gameContextData.activeTeam)
            ?.name
        }.png`
      );
      doc.addImage(
        `/assets/teams-logos/${
          appData?.teams?.find((e) => e._id === gameContextData.activeTeam)
            ?.name
        }.png`,
        "PNG",
        pageWidth / 2 - 275,
        500,
        150,
        (imgProps.height * 150) / imgProps.width
      );
      doc.addImage(
        `/assets/logo${
          appData?.teams?.find((e) => e._id === gameContextData.activeTeam)
            ?.league
        }.png`,
        "PNG",
        pageWidth / 2 - 75 + 200,
        500,
        120,
        (imgProps.height * 120) / imgProps.width
      );
    }

    await doc.save(
      `${
        appData?.teams.filter((e) => e._id === gameContextData.activeTeam)[0]
          ?.name
      } vs ${
        appData?.teams.filter((e) => e._id === gameContextData.activeRival)[0]
          ?.name
      } - Temporada ${gameContextData.activeSeason}.pdf`
    );
    setIsExporting(false);
    elements.forEach((element) => (element.style.border = "unset"));

    elements.forEach((element) => {
      if (!element.id.includes("Image")) element.style.border = "unset";
      if (
        element.id.includes("ComparisonPlayerChart") ||
        element.id.includes("PlayerResume") ||
        element.id.includes("PlayerStatistics")
      ) {
        const select = element.querySelector("select");
        if (select) {
          select.style.display = "unset";
        }
      }
    });
    linkButtons.forEach((link) => (link.style.display = "unset"));
    linkText.forEach((link) => (link.style.display = "unset"));
    document.getElementById("print-buttons-options").classList.remove("active");
    imageButtons.forEach((img) => (img.style.display = "unset"));
  };

  const getFilteredGames = (team) => {
    return gameContextData.games?.filter((game) => {
      const isTeamGame = game.local_team === team || game.visit_team === team;
      const isH2HGame =
        (game.local_team === gameContextData.activeTeam &&
          game.visit_team === gameContextData.activeRival) ||
        (game.local_team === gameContextData.activeRival &&
          game.visit_team === gameContextData.activeTeam);

      return (
        (gameContextData.isH2HActive ? isH2HGame : isTeamGame) &&
        game.season === gameContextData.activeSeason &&
        (gameContextData.activePhase === null ||
          game.stage === gameContextData.activePhase) &&
        game.category === gameContextData.activeLeague
      );
    });
  };

  const calculateMetric = (team, metric) => {
    const filteredGames = getFilteredGames(team);
    return calculatePercj(filteredGames, team, metric).toFixed(1);
  };

  const handleSaveAnalisys = useCallback(
    (collection) => {
      if (clipName !== null) {
        const userId = secureLocalStorage.getItem("userId");
        const clipId = generateId();
        let user = JSON.parse(sessionUser) || {};
        const newClip = {
          id: clipId,
          templateId: slug,
          userId: userId,
          name: clipName,
          sharedWith: [],
          config: {
            filters: {
              activeLeague: gameContextData.activeLeague,
              activePhase: gameContextData.activePhase,
              activeSeason: gameContextData.activeSeason,
              activeTeam: gameContextData.activeTeam,
              activeRival: gameContextData.activeRival,
              baseTeam: gameContextData.baseTeam,
              isH2HActive: gameContextData.isH2HActive,
              statInX: gameContextData.statInX,
              statInY: gameContextData.statInY,
            },
          },
          createdAt: formatDate(new Date()).DateFormatted,
        };
        firebase
          .database()
          .ref(`/users/0/${userId}/analyticsClips/${clipId}`)
          .set(newClip)
          .then(async () => {
            let userBKP = user;
            if (
              userBKP.analyticsClips !== undefined &&
              userBKP.analyticsClips !== null
            ) {
              userBKP.analyticsClips[clipId] = newClip;
            } else {
              userBKP.analyticsClips = [];
              userBKP.analyticsClips.push(newClip);
            }

            if (Array.isArray(collectionName) && collectionName.length > 0) {
              await handleSaveCollection(newClip);
            } else {
              await sessionStorage.setItem("user", JSON.stringify(userBKP));
              secureLocalStorage.setItem("user", userBKP);
              secureLocalStorage.setItem("userLocal", userBKP);
            }
            addModal({
              modal: "SuccessModal",
              linkTo: `/app/analytics-dashboard/clips`,
            });
          })
          .catch((error) => {
            console.error("Error saving analysis clip:", error);
            addModal({
              modal: "ErrorModal",
              linkTo: `/app/analytics-dashboard`,
            });
          });
      }
    },
    [sessionUser, gameContextData, clipName, collectionName]
  );

  function makeClipSlug(senderId, clipId, templateId) {
    return `C-${senderId}__${clipId}__${templateId}`;
  }

  async function shareClipWithEmail(ownerId, clipId, emails) {
    const slug = makeClipSlug(ownerId, clipId, clip.templateId);
    let user = JSON.parse(sessionUser) || {};
    let userBKP = user;

    const existingAccounts = userBKP.relatedAccounts || [];

    // Filtrar solo los correos nuevos
    const newAccounts = emails.filter(
      (email) => !existingAccounts.includes(email)
    );

    if (newAccounts.length > 0) {
      const updatedAccounts = [...existingAccounts, ...newAccounts];
      userBKP.relatedAccounts = updatedAccounts;

      await sessionStorage.setItem("user", JSON.stringify(userBKP));
      secureLocalStorage.setItem("user", userBKP);
      secureLocalStorage.setItem("userLocal", userBKP);
    }

    try {
      const res = await fetch(
        "https://deportactico-vercel-serverless.vercel.app/api/shareClip",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fromName: user.name,
            senderId: userId,
            toEmails: emails,
            clipName: clip.name,
            shareLink: `https://deportacticoapp.vercel.app//app/SharedBoard/${slug}`,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Error compartiendo el clip");
      }

      setIsShare(false);
      addModal({ modal: "SuccessModal", linkTo: `` });
    } catch (err) {
      console.error("❌ Error fetching shared clip:", err);
      addModal({ modal: "ErrorModal", linkTo: `` });
    }
  }

  const handleSaveCollection = async (clip) => {
    try {
      const clipIdToUse = clip?.id ? clip.id : cleanSlug;

      const userCollections = parsedUser.clipCollections || {};
      const allCollections = Object.values(userCollections);

      const selectedCollections = allCollections.filter((c) =>
        collectionName.includes(c.name)
      );

      const unselectedCollections = allCollections.filter(
        (c) => !collectionName.includes(c.name)
      );

      const newCollectionNames = collectionName.filter(
        (name) => !allCollections.some((c) => c.name === name)
      );

      for (const name of newCollectionNames) {
        const newId = generateId();

        const newCollection = {
          id: newId,
          name,
          ownerId: userId,
          sharedWith: [],
          clipIds: [clipIdToUse],
          createdAt: new Date().toISOString(),
        };

        await firebase
          .database()
          .ref(`/users/0/${userId}/clipCollections/${newId}`)
          .set(newCollection);

        parsedUser.clipCollections[newId] = newCollection;

        selectedCollections.push(newCollection);
      }

      for (const col of selectedCollections) {
        const updatedClipIds = Array.from(
          new Set([...(col.clipIds || []), clipIdToUse])
        );

        await firebase
          .database()
          .ref(`/users/0/${userId}/clipCollections/${col.id}`)
          .update({ clipIds: updatedClipIds });

        parsedUser.clipCollections[col.id].clipIds = updatedClipIds;
      }

      for (const col of unselectedCollections) {
        const updatedClipIds = (col.clipIds || []).filter(
          (id) => id !== clipIdToUse
        );

        await firebase
          .database()
          .ref(`/users/0/${userId}/clipCollections/${col.id}`)
          .update({ clipIds: updatedClipIds });

        parsedUser.clipCollections[col.id].clipIds = updatedClipIds;
      }

      // Guardar clip en local
      if (clip) {
        parsedUser.analyticsClips = parsedUser.analyticsClips || {};
        parsedUser.analyticsClips[clip.id] = clip;
      }

      sessionStorage.setItem("user", JSON.stringify(parsedUser));
      secureLocalStorage.setItem("user", JSON.stringify(parsedUser));
      secureLocalStorage.setItem("userLocal", JSON.stringify(parsedUser));

      setIsCollection(false);
      addModal({
        modal: "SuccessModal",
        linkTo: `/app/analytics-dashboard/colections`,
      });
    } catch (error) {
      console.error("❌ Error guardando en colecciones:", error);
      addModal({ modal: "ErrorModal" });
    }
  };

  //MULTIPLE EMAILS
  const handleSend = async () => {
    if (sharedData.emails.length === 0)
      return alert("Agrega al menos un correo válido.");
    await shareClipWithEmail(userId, cleanSlug, sharedData.emails);
    setIsShare(false);
  };

  if (isLoading) {
    return <Loader />;
  }

  const userPlan = parsedUser?.plan;
  const sharedClips = parsedUser?.sharedClips || [];
  const sharedCollections = parsedUser?.sharedCollections || [];

  const isInSharedClips = Array.isArray(sharedClips)
    ? sharedClips.some((clip) => clip.id === cleanSlug)
    : Object.values(sharedClips).some((clip) => clip.id === cleanSlug);

  const isInSharedCollections = Array.isArray(sharedCollections)
    ? sharedCollections.some((collection) =>
        Array.isArray(collection.clipIds)
          ? collection.clipIds.some((clip) => clip.id === cleanSlug)
          : Object.keys(collection.clips || {}).includes(cleanSlug)
      )
    : Object.values(sharedCollections).some((collection) =>
        Array.isArray(collection.clipIds)
          ? collection.clipIds.some((clip) => clip.id === cleanSlug)
          : Object.keys(collection.clips || {}).includes(cleanSlug)
      );

  const hasAccessToClip =
    userPlan !== "Basic" ||
    (userPlan === "Basic" && (isInSharedClips || isInSharedCollections));

  return (
    <Layout>
      <SharePupupComponent
        isActive={isShare}
        popupStep={popupStep}
        setIsShare={setIsShare}
        onChange={(data) => {
          setSharedData(data);
        }}
        onSend={() => {
          handleSend();
        }}
        setClipName={setClipName}
        clipName={clipName}
        handleSaveAnalisys={handleSaveAnalisys}
        clipCollections={
          parsedUser?.clipCollections
            ? Object.values(parsedUser.clipCollections)
            : null
        }
        setCollectionName={setCollectionName}
        handleSaveCollection={handleSaveCollection}
        collectionName={collectionName}
      />

      {hasAccessToClip ? (
        <Container>
          {isExporting ? (
            <div className="export-image-loader">
              <p className="loading">cargando</p>
            </div>
          ) : null}
          <div
            className={`headder-container no-print  ${
              isExporting ? "exporting" : null
            }`}
            style={{ display: isClip ? "none" : "block" }}
          >
            <h1>Analiticas</h1>
            <GameContextSelector
              appData={appData}
              typeOfSelector="analytics"
              onChange={handleGameContextChange}
              initialFilters={isClip ? clip?.config?.filters : null}
            />
          </div>
          {appData !== null &&
          (gameContextData.activeTeam !== null ||
            gameContextData.activeRival !== null) &&
          gameContextData.activeLeague !== null &&
          gameContextData.activeSeason !== null &&
          appData.teams !== null &&
          appData.players !== null &&
          slug === "/app/analytics" &&
          gameContextData.games?.filter(
            (game) =>
              (game.local_team === gameContextData.activeTeam ||
                game.visit_team === gameContextData.activeTeam) &&
              game.season === gameContextData.activeSeason &&
              (gameContextData.activePhase === null ||
                game.stage === gameContextData.activePhase)
          ).length > 0 &&
          gameContextData.games !== null ? (
            <div id="data-container" className="data-container">
              <div className="hidden-logo">
                <img
                  className="navbarLogo for-print"
                  src="../../assets/Logo.png"
                  alt="deportactico logo"
                  style={{ width: "40px" }}
                />
                <h1>Deportactico</h1>
              </div>
              <button
                className="btn-primary ghost no-print"
                onClick={(e) => generateImageCanvas()}
                style={{ marginTop: "1rem" }}
              >
                Guardar informe
              </button>
              <div className="container-flex team-container">
                <p>
                  <b>Equipo: </b>{" "}
                  <a href={`/teamProfile/${gameContextData.activeTeam}`}>
                    {
                      appData?.teams.filter(
                        (e) => e._id === gameContextData.activeTeam
                      )[0].name
                    }
                  </a>
                </p>
                <p>
                  <b>Posición: </b>{" "}
                  {calculateTeamPosition(gameContextData.activeTeam) + 1}
                </p>
              </div>
              <div className="container-flex team-container">
                {gameContextData.activeRival !== null ? (
                  <>
                    <p>
                      <b>Equipo: </b>{" "}
                      <a href={`/teamProfile/${gameContextData.activeRival}`}>
                        {
                          appData?.teams.filter(
                            (e) => e._id === gameContextData.activeRival
                          )[0].name
                        }
                      </a>
                    </p>
                    <p>
                      <b>Posición: </b>{" "}
                      {calculateTeamPosition(gameContextData.activeRival) + 1}
                    </p>{" "}
                  </>
                ) : (
                  <p>Selecciona rival</p>
                )}
              </div>
              {JSON.parse(sessionUser).plan === "owner" ? (
                <div
                  className="probability-container no-print"
                  style={{ width: "100%" }}
                >
                  <h3>
                    Probabilidades de victoria:{" "}
                    {calculateProbability().toFixed(1)}%
                  </h3>
                  <div
                    className="probability-graph"
                    style={{
                      background: `linear-gradient(to right, #ff0000 ${calculateProbability()}%, #6f7a80 ${
                        -100 - calculateProbability()
                      }%)`,
                    }}
                  ></div>
                </div>
              ) : null}
              <div className="container-flex boxshadow stats-box">
                <table>
                  <thead>
                    <tr>
                      <th scope="col"></th>
                      <th scope="col">
                        {
                          appData?.teams.filter(
                            (e) => e._id === gameContextData.activeTeam
                          )[0].name
                        }
                      </th>
                      {gameContextData.activeRival !== null ? (
                        <th scope="col">
                          {
                            appData?.teams.filter(
                              (e) => e._id === gameContextData.activeRival
                            )[0].name
                          }
                        </th>
                      ) : null}
                    </tr>
                    <tr>
                      <th scope="col">Metrica</th>
                      <th scope="col">Temporada</th>
                      {gameContextData.activeRival !== null ? (
                        <th scope="col">Temporada</th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>PJ</td>
                      <td>
                        {!gameContextData.isH2HActive &&
                          gameContextData.games.filter(
                            (game) =>
                              (game.local_team === gameContextData.activeTeam ||
                                game.visit_team ===
                                  gameContextData.activeTeam) &&
                              game.season === gameContextData.activeSeason &&
                              (gameContextData.activePhase === null ||
                                game.stage === gameContextData.activePhase) &&
                              game.category === gameContextData.activeLeague
                          ).length}
                        {gameContextData.isH2HActive &&
                          gameContextData.games.filter(
                            (game) =>
                              ((game.local_team ===
                                gameContextData.activeTeam &&
                                game.visit_team ===
                                  gameContextData.activeRival) ||
                                (game.local_team ===
                                  gameContextData.activeRival &&
                                  game.visit_team ===
                                    gameContextData.activeTeam)) &&
                              game.season === gameContextData.activeSeason &&
                              (gameContextData.activePhase === null ||
                                game.stage === gameContextData.activePhase) &&
                              game.category === gameContextData.activeLeague
                          ).length}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {!gameContextData.isH2HActive &&
                            gameContextData.games.filter(
                              (game) =>
                                (game.local_team ===
                                  gameContextData.activeRival ||
                                  game.visit_team ===
                                    gameContextData.activeRival) &&
                                game.season === gameContextData.activeSeason &&
                                (gameContextData.activePhase === null ||
                                  game.stage === gameContextData.activePhase) &&
                                game.category === gameContextData.activeLeague
                            ).length}
                          {gameContextData.isH2HActive &&
                            gameContextData.games.filter(
                              (game) =>
                                ((game.local_team ===
                                  gameContextData.activeTeam &&
                                  game.visit_team ===
                                    gameContextData.activeRival) ||
                                  (game.local_team ===
                                    gameContextData.activeRival &&
                                    game.visit_team ===
                                      gameContextData.activeTeam)) &&
                                game.season === gameContextData.activeSeason &&
                                (gameContextData.activePhase === null ||
                                  game.stage === gameContextData.activePhase) &&
                                game.category === gameContextData.activeLeague
                            ).length}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>PTS</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "PTS")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(gameContextData.activeRival, "PTS")}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>FGM</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "FG")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(gameContextData.activeRival, "FG")}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>FGA</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "FGA")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(gameContextData.activeRival, "FGA")}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>FG%</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "FGP")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(gameContextData.activeRival, "FGP")}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>3PM</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "THREEP")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(
                            gameContextData.activeRival,
                            "THREEP"
                          )}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>3PA</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "THREEPA")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(
                            gameContextData.activeRival,
                            "THREEPA"
                          )}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>3P%</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "THREEPP")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(
                            gameContextData.activeRival,
                            "THREEPP"
                          )}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>2PM</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "TWOOP")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(
                            gameContextData.activeRival,
                            "TWOOP"
                          )}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>2PA</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "TWOOPA")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(
                            gameContextData.activeRival,
                            "TWOOPA"
                          )}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>2P%</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "TWOOPP")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(
                            gameContextData.activeRival,
                            "TWOOPP"
                          )}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>FTM</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "FT")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(gameContextData.activeRival, "FT")}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>FTA</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "FTA")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(gameContextData.activeRival, "FTA")}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>FT%</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "FTP")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(gameContextData.activeRival, "FTP")}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>OR</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "ORB")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(gameContextData.activeRival, "ORB")}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>DR</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "DRB")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(gameContextData.activeRival, "DRB")}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>REB</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "REB")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(gameContextData.activeRival, "REB")}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>ASS</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "ASS")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(gameContextData.activeRival, "ASS")}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>STL</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "STL")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(gameContextData.activeRival, "STL")}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>BLK</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "BLK")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(gameContextData.activeRival, "BLK")}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>PER</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "TORP")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(gameContextData.activeRival, "TORP")}
                        </td>
                      ) : null}
                    </tr>
                    <tr>
                      <td>PF</td>
                      <td>
                        {calculateMetric(gameContextData.activeTeam, "FL")}
                      </td>
                      {gameContextData.activeRival !== null ? (
                        <td>
                          {calculateMetric(gameContextData.activeRival, "FL")}
                        </td>
                      ) : null}
                    </tr>
                  </tbody>
                </table>
              </div>
              {dataSets !== null ? (
                <div className="container-flex boxshadow radar-chart-box pb-after">
                  <Radar data={dataSets} options={options} />
                </div>
              ) : null}
              {shootingData !== null ? (
                <div className="container-flex boxshadow shooting-radar-chart-box">
                  <h3>Porcentaje de tiros</h3>
                  <Bar options={optionsBarChart} data={shootingData} />
                </div>
              ) : null}
              {reboundingData !== null ? (
                <div className="container-flex boxshadow shooting-radar-chart-box">
                  <h3>Porcentaje de rebotes</h3>
                  <Bar options={optionsBarChart} data={reboundingData} />
                </div>
              ) : null}
              {assistsData !== null ? (
                <div className="container-flex boxshadow shooting-radar-chart-box">
                  <h3>Porcentaje de asistencias</h3>
                  <Bar options={optionsBarChart} data={assistsData} />
                </div>
              ) : null}
              {extraData !== null ? (
                <div className="container-flex boxshadow shooting-radar-chart-box">
                  <h3>Porcentaje de extras</h3>
                  <Bar options={optionsBarChart} data={extraData} />
                </div>
              ) : null}
            </div>
          ) : appData !== null &&
            gameContextData.activeTeam !== null &&
            gameContextData.activeLeague !== null &&
            gameContextData.activeRival !== null &&
            gameContextData.activeSeason !== null &&
            appData.teams !== null &&
            appData.players !== null &&
            gameContextData.games?.filter(
              (game) =>
                (game.local_team === gameContextData.activeTeam ||
                  game.visit_team === gameContextData.activeTeam) &&
                game.season === gameContextData.activeSeason &&
                (gameContextData.activePhase === null ||
                  game.stage === gameContextData.activePhase)
            ).length === 0 &&
            gameContextData.games !== null ? (
            <div className="no-data-messagge">
              <h2>No hay información</h2>
            </div>
          ) : appData !== null &&
            (gameContextData.activeTeam !== null ||
              gameContextData.activeRival !== null) &&
            gameContextData.activeLeague !== null &&
            gameContextData.activeSeason !== null &&
            appData.teams !== null &&
            appData.players !== null &&
            gameContextData.games?.filter(
              (game) =>
                (game.local_team === gameContextData.activeTeam ||
                  game.visit_team === gameContextData.activeTeam) &&
                game.season === gameContextData.activeSeason &&
                (gameContextData.activePhase === null ||
                  game.stage === gameContextData.activePhase)
            ).length > 0 &&
            gameContextData.games !== null &&
            !isClip &&
            slug !== "/app/analytics" ? (
            <>
              <button
                className="btn-primary ghost no-print full-width-m"
                onClick={(e) => {
                  setIsShare(true);
                  setPopupStep("save-isSetClipName");
                }}
                style={{ marginTop: "1rem", marginLeft: "5%" }}
              >
                Guardar
              </button>
              <button
                className="btn-primary ghost no-print full-width-m"
                onClick={() => {
                  const el = document.getElementById("print-buttons-options");
                  if (el) {
                    el.classList.toggle("active");
                  }
                }}
                style={{ marginTop: "1rem" }}
              >
                Exportar informe
              </button>
              <div id="print-buttons-options">
                <button
                  className="btn-primary ghost no-print"
                  onClick={(e) => {
                    generateImageCanvas();
                  }}
                >
                  Como imagen
                </button>
                <button
                  className="btn-primary ghost no-print"
                  onClick={(e) => handleSplitAndDownload()}
                >
                  Como pdf
                </button>
              </div>

              <div className="personilized-dashboard">
                <div
                  className="dashboard-content-container"
                  ref={containerRef}
                  id="data-container"
                  style={{
                    height: calculateMaxHeight(
                      JSON.parse(sessionUser).analyticsBoards[slug].data
                    ),
                    margin: "0 auto",
                  }}
                >
                  {JSON.parse(sessionUser).analyticsBoards[slug].data.map(
                    (item, i) => {
                      const baseId = item.id.split("-")[0];
                      const matchedItem = defaultItems.find(
                        (defaultItem) => defaultItem.id === baseId
                      );

                      if (!matchedItem) {
                        return (
                          <div key={item.id}>
                            Component not found for {item.id}
                          </div>
                        );
                      }
                      return (
                        <CanvasElement
                          key={item.id}
                          id={item.id}
                          label={item.label}
                          x={item.x}
                          y={item.y}
                          width={item.width}
                          height={item.height}
                          boxShadow={true}
                          isAnalitycsPage={true}
                        >
                          {typeof matchedItem.content === "string" ? (
                            <span>{matchedItem.content}</span>
                          ) : (
                            React.createElement(
                              matchedItem.content.type || "div",
                              {
                                ...matchedItem.content.props,
                                games: gameContextData.isH2HActive
                                  ? gameContextData.games.filter(
                                      (game) =>
                                        ((game.local_team ===
                                          gameContextData.activeTeam &&
                                          game.visit_team ===
                                            gameContextData.activeRival) ||
                                          (game.local_team ===
                                            gameContextData.activeRival &&
                                            game.visit_team ===
                                              gameContextData.activeTeam)) &&
                                        game.season ===
                                          gameContextData.activeSeason &&
                                        (gameContextData.activePhase === null ||
                                          game.stage ===
                                            gameContextData.activePhase) &&
                                        game.category ===
                                          gameContextData.activeLeague
                                    )
                                  : gameContextData.games,
                                activePhase: gameContextData.activePhase,
                                activeSeason: gameContextData.activeSeason,
                                activeLeague: gameContextData.activeLeague,
                                baseTeam: gameContextData.baseTeam,
                                activeRival: gameContextData.activeRival,
                                activeTeam: gameContextData.activeTeam,
                                isH2HActive: gameContextData.isH2HActive,
                                teams: appData.teams,
                                players: appData?.players?.filter((player) => {
                                  const isRegisteredInSeason =
                                    player.teams_history?.some(
                                      (item) =>
                                        item.season ===
                                          gameContextData.activeSeason &&
                                        item.teamId ===
                                          gameContextData.baseTeam &&
                                        (item.end_date === "active" ||
                                          item.end_date === undefined)
                                    );

                                  return isRegisteredInSeason;
                                }),
                                data: item.id.includes("Table")
                                  ? item.tableData
                                  : item.id.includes("ComparisonBarChart")
                                  ? item.selectedOption
                                  : item.id.includes("Featured")
                                  ? item.selectedOption
                                  : item.id.includes("Note")
                                  ? item.noteContent
                                  : item.id.includes("Title")
                                  ? item.titleContent
                                  : null,
                                listType: item.listType,
                              }
                            )
                          )}
                        </CanvasElement>
                      );
                    }
                  )}
                </div>
              </div>
            </>
          ) : null}
          {isClip &&
          clip !== null &&
          gameContextData !== null &&
          gameContextData.games !== null &&
          gameContextData?.games?.length > 0 ? (
            <div className="personilized-dashboard">
              {clip?.clipId === undefined &&
              parsedUser.analyticsClips &&
              Object.values(parsedUser.analyticsClips)?.find(
                (item) => item.id === cleanSlug
              ) ? (
                <>
                  <button
                    className="btn-primary ghost no-print"
                    onClick={(e) => {
                      setIsShare(true);
                      setPopupStep("isShare");
                    }}
                  >
                    Compartir
                  </button>
                  <button
                    className="btn-primary ghost no-print"
                    onClick={(e) => {
                      setIsShare(true);
                      setPopupStep("save-isSetCollection");
                    }}
                  >
                    Agregar a coleccón
                  </button>
                </>
              ) : null}
              <button
                className="btn-primary ghost no-print full-width-m"
                onClick={() => {
                  const el = document.getElementById("print-buttons-options");
                  if (el) {
                    el.classList.toggle("active");
                  }
                }}
                style={{ marginTop: "1rem" }}
              >
                Exportar informe
              </button>
              <div id="print-buttons-options">
                <button
                  className="btn-primary ghost no-print"
                  onClick={(e) => {
                    generateImageCanvas();
                  }}
                >
                  Como imagen
                </button>
                <button
                  className="btn-primary ghost no-print"
                  onClick={(e) => handleSplitAndDownload()}
                >
                  Como pdf
                </button>
              </div>

              <div
                className="dashboard-content-container"
                ref={containerRef}
                id="data-container"
                style={{
                  height: calculateMaxHeight(clip?.board.data),
                  margin: "0 auto",
                }}
              >
                {clip.board.data.map((item, i) => {
                  const baseId = item.id.split("-")[0];
                  const matchedItem = defaultItems.find(
                    (defaultItem) => defaultItem.id === baseId
                  );

                  if (!matchedItem) {
                    return (
                      <div key={item.id}>Component not found for {item.id}</div>
                    );
                  }
                  return (
                    <CanvasElement
                      key={item.id}
                      id={item.id}
                      label={item.label}
                      x={item.x}
                      y={item.y}
                      width={item.width}
                      height={item.height}
                      boxShadow={true}
                      isAnalitycsPage={true}
                    >
                      {typeof matchedItem.content === "string" ? (
                        <span>{matchedItem.content}</span>
                      ) : (
                        React.createElement(matchedItem.content.type || "div", {
                          ...matchedItem.content.props,
                          games: gameContextData.isH2HActive
                            ? gameContextData.games.filter(
                                (game) =>
                                  ((game.local_team ===
                                    gameContextData.activeTeam &&
                                    game.visit_team ===
                                      gameContextData.activeRival) ||
                                    (game.local_team ===
                                      gameContextData.activeRival &&
                                      game.visit_team ===
                                        gameContextData.activeTeam)) &&
                                  game.season ===
                                    gameContextData.activeSeason &&
                                  (gameContextData.activePhase === null ||
                                    game.stage ===
                                      gameContextData.activePhase) &&
                                  game.category === gameContextData.activeLeague
                              )
                            : gameContextData.games,
                          activePhase: gameContextData.activePhase,
                          activeSeason: gameContextData.activeSeason,
                          activeLeague: gameContextData.activeLeague,
                          baseTeam: gameContextData.baseTeam,
                          activeRival: gameContextData.activeRival,
                          activeTeam: gameContextData.activeTeam,
                          isH2HActive: gameContextData.isH2HActive,
                          teams: appData.teams,
                          players: appData?.players?.filter((player) => {
                            const isRegisteredInSeason =
                              player.teams_history?.some(
                                (item) =>
                                  item.season ===
                                    gameContextData.activeSeason &&
                                  item.teamId === gameContextData.baseTeam &&
                                  (item.end_date === "active" ||
                                    item.end_date === undefined)
                              );

                            return isRegisteredInSeason;
                          }),
                          data: item.id.includes("Table")
                            ? item.tableData
                            : item.id.includes("ComparisonBarChart")
                            ? item.selectedOption
                            : item.id.includes("Featured")
                            ? item.selectedOption
                            : item.id.includes("Note")
                            ? item.noteContent
                            : item.id.includes("Title")
                            ? item.titleContent
                            : null,
                          listType: item.listType,
                        })
                      )}
                    </CanvasElement>
                  );
                })}
              </div>
            </div>
          ) : null}
        </Container>
      ) : (
        <UpgradeContainer />
      )}
    </Layout>
  );
}

export default Analytics;
